import React,
{
    useEffect,
    useRef
}
from 'react';

const iconAssetCache = new Map();

function parseAtlas(xmlText)
{
    try
    {
        const dom = new DOMParser().parseFromString(xmlText, 'application/xml');
        const subs = Array.from(dom.getElementsByTagName('SubTexture'));
        return subs.map((s, index) => (
        {
            name: s.getAttribute('name') || '',
            x: parseInt(s.getAttribute('x') || '0', 10),
            y: parseInt(s.getAttribute('y') || '0', 10),
            w: parseInt(s.getAttribute('width') || s.getAttribute('w') || '0', 10),
            h: parseInt(s.getAttribute('height') || s.getAttribute('h') || '0', 10),
            order: index,
        }));
    }
    catch (e)
    {}
    return [];
}

function loadRGLIconAssets(basePath)
{
    if (iconAssetCache.has(basePath)) return iconAssetCache.get(basePath);

    const promise = Promise.all([
        new Promise((resolve, reject) =>
        {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = `${basePath}.png`;
        }),
        fetch(`${basePath}.xml`).then((r) => r.text())
    ]).then(([img, xmlText]) => ({
        image: img,
        frames: parseAtlas(xmlText)
    })).catch((error) =>
    {
        iconAssetCache.delete(basePath);
        throw error;
    });

    iconAssetCache.set(basePath, promise);
    return promise;
}

function getFrameSequence(frames, framePrefix)
{
    if (!framePrefix || !frames.length) return frames;
    const filtered = frames.filter((frame) => frame.name.startsWith(framePrefix));
    if (!filtered.length) return frames;
    const parseDigits = (name) =>
    {
        const match = name.match(/(\d+)$/);
        return match ? Number(match[1]) : 0;
    };
    return filtered.slice().sort((a, b) =>
    {
        const aIndex = parseDigits(a.name);
        const bIndex = parseDigits(b.name);
        if (aIndex !== bIndex) return aIndex - bIndex;
        return a.order - b.order;
    });
}

// RGLIcon, sprite-sheet animator driven by an XML atlas
// needs a basePath for the image and XML files
// (default '/RGL-Icon.png' and '/RGL-Icon.xml'). Animates at `fps`, dont forget
export default function RGLIcon(
{
    basePath = '/RGL-Icon',
    size = 120,
    width,
    height,
    fps = 30,
    frameIntervalMs,
    syncTimeMs,
    style = {},
    framePrefix,
    frameIndices,
    loop = true,
    onSequenceComplete
})
{
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const framesRef = useRef([]);
    const timerRef = useRef(null);
    const rafRef = useRef(null);
    const idxRef = useRef(0);
    const onSequenceCompleteRef = useRef(onSequenceComplete);

    useEffect(() =>
    {
        onSequenceCompleteRef.current = onSequenceComplete;
    }, [onSequenceComplete]);

    useEffect(() =>
    {
        let mounted = true;

        loadRGLIconAssets(basePath)
            .then(({ image, frames }) =>
            {
                if (!mounted) return;
                imageRef.current = image;
                const baseFrames = getFrameSequence(frames, framePrefix);
                const selectedFrames = Array.isArray(frameIndices) && frameIndices.length ?
                    frameIndices
                        .map((frameIndex) => baseFrames[frameIndex])
                        .filter(Boolean) :
                    baseFrames;

                framesRef.current = selectedFrames.length ? selectedFrames : (
                    frames.length ? frames : [
                    {
                        x: 0,
                        y: 0,
                        w: image.width,
                        h: image.height
                    }]
                );
                startLoop();
            })
            .catch(() =>
            {
                // if loading atlas fails, try only image
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () =>
                {
                    if (!mounted) return;
                    imageRef.current = img;
                    framesRef.current = [
                    {
                        x: 0,
                        y: 0,
                        w: img.width,
                        h: img.height
                    }];
                    startLoop();
                };
                img.onerror = () => {};
                img.src = `${basePath}.png`;
            });

        const startLoop = () =>
        {
            const interval = Number.isFinite(frameIntervalMs) && frameIntervalMs > 0 ?
                frameIntervalMs :
                (1000 / Math.max(1, fps));

            if (rafRef.current)
            {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }

            if (timerRef.current)
            {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            const advanceFrame = () =>
            {
                const frames = framesRef.current;
                const nextIdx = idxRef.current + 1;
                const finished = nextIdx >= frames.length;
                if (finished)
                {
                    if (loop)
                    {
                        idxRef.current = 0;
                    }
                    else
                    {
                        idxRef.current = frames.length - 1;
                    }
                }
                else
                {
                    idxRef.current = nextIdx;
                }
                drawFrame();
                if (finished && !loop)
                {
                    const callback = onSequenceCompleteRef.current;
                    if (typeof callback === 'function') callback();
                    return;
                }
            };

            if (typeof syncTimeMs === 'function' && Number.isFinite(interval) && interval > 0)
            {
                const syncTick = () =>
                {
                    const frames = framesRef.current;
                    const syncedMs = syncTimeMs();

                    if (frames.length && Number.isFinite(syncedMs) && syncedMs >= 0)
                    {
                        idxRef.current = Math.floor(syncedMs / interval) % frames.length;
                    }

                    drawFrame();
                    rafRef.current = requestAnimationFrame(syncTick);
                };

                drawFrame();
                rafRef.current = requestAnimationFrame(syncTick);
                return;
            }

            drawFrame();
            timerRef.current = setInterval(advanceFrame, interval);
        };

        const drawFrame = () =>
        {
            const canvas = canvasRef.current;
            const img = imageRef.current;
            const frames = framesRef.current;
            if (!canvas || !img || !frames.length) return;
            const ctx = canvas.getContext('2d');
            const frame = frames[idxRef.current % frames.length];
            const cw = canvas.width;
            const ch = canvas.height;
            // ensure we never keep a stale DPR transform (which can cause cropping/"zoomed" frames)
            try
            {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
            }
            catch (e)
            {}
            ctx.clearRect(0, 0, cw, ch);
            try
            {
                // draw source frame to fit canvas
                ctx.drawImage(img, frame.x, frame.y, frame.w, frame.h, 0, 0, cw, ch);
            }
            catch (e)
            {}
        };

        return () =>
        {
            mounted = false;
            if (timerRef.current)
            {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (rafRef.current)
            {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [basePath, fps, frameIntervalMs, syncTimeMs, framePrefix, frameIndices, loop]);

    // keep canvas sized to `size`, its a square
    useEffect(() =>
    {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resolvedWidth = Number.isFinite(width) ? width : size;
        const resolvedHeight = Number.isFinite(height) ? height : size;

        const applySize = () =>
        {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = Math.round(resolvedWidth * dpr);
            canvas.height = Math.round(resolvedHeight * dpr);
            canvas.style.width = `${resolvedWidth}px`;
            canvas.style.height = `${resolvedHeight}px`;

            // draw in device pixels (canvas.width/height) with identity transform
            // (we already accounted for DPR via backing resolution)
            const ctx = canvas.getContext('2d');
            try
            {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
            }
            catch (e)
            {}
        };

        applySize();
        // zoom/DPR changes commonly fire a resize; re-apply backing resolution
        window.addEventListener('resize', applySize);
        return () => window.removeEventListener('resize', applySize);
    }, [height, size, width]);

    return ( <canvas ref = {
            canvasRef
        }
        style = {
            {
                width: Number.isFinite(width) ? width : size,
                height: Number.isFinite(height) ? height : size,
                display: 'block',
                ...style,
            }
        }
        />
    );
}
